/**
 * ChangePasswordInlineDialog — authenticated password change.
 *
 * Extracted from TopNav (2026-06-26) into a shared component so the worker
 * portal (Sign-in & security) and the admin TopNav user menu use ONE
 * implementation (svdy-rules §10.5 — no drift between two copies).
 *
 * Calls svdy-authorization POST /v1/auth/change-password (current + new).
 * On success the auth server revokes refresh tokens; we just close (the
 * existing session token stays valid until expiry — matches prior behavior).
 *
 * Mobile twin: svdy-mobile/app/change-password.tsx (same auth endpoint).
 */
import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Stack,
  TextField, Button, Typography, IconButton, InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import PasswordRequirements, { passwordMeetsRules } from './PasswordRequirements';

const ChangePasswordInlineDialog: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  React.useEffect(() => {
    if (open) {
      setCurrent(''); setNext(''); setConfirm('');
      setError(null); setSuccess(false);
      setShowCurrent(false); setShowNext(false); setShowConfirm(false);
    }
  }, [open]);

  const submit = async () => {
    setError(null);
    // All four requirements, not just the length. Checking only the length let
    // the user submit `password` — which satisfies the one hint the field used to
    // show — and get refused by the server with no indication of what was
    // missing. `passwordMeetsRules` is the same rule set the checklist renders,
    // so the button and the ticks can never disagree.
    if (!passwordMeetsRules(next)) {
      setError(t('common:changePassword.requirementsNotMet'));
      return;
    }
    if (next !== confirm) { setError(t('common:changePassword.mismatch')); return; }

    setSubmitting(true);
    try {
      const authUrl = process.env.REACT_APP_AUTH_URL || 'http://127.0.0.1:8000';
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const res = await fetch(`${authUrl}/v1/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ current_password: current, new_password: next }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || t('common:feedback.actionFailed'));
      }
      setSuccess(true);
      setTimeout(() => { onClose(); }, 1500);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('common:feedback.actionFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const eye = (shown: boolean, toggle: () => void) => (
    <InputAdornment position="end">
      <IconButton size="small" onClick={toggle} edge="end" tabIndex={-1}
        aria-label={shown ? 'Hide password' : 'Show password'}>
        {shown ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Dialog open={open} onClose={() => !submitting && onClose()} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 700, fontSize: '1.0625rem', pb: 1 }}>{t('common:changePassword.title')}</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        {success ? (
          <Box sx={{ py: 2, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600 }}>{t('common:changePassword.success')}</Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            <TextField
              label={t('common:changePassword.currentPassword')}
              type={showCurrent ? 'text' : 'password'}
              size="small" fullWidth
              value={current} onChange={(e) => setCurrent(e.target.value)}
              disabled={submitting}
              InputProps={{ endAdornment: eye(showCurrent, () => setShowCurrent((v) => !v)) }}
            />
            {/* The field and its checklist are ONE Stack child, not two.
                As siblings the block got the Stack's 16px above it AND 16px
                below, so it read as an independent paragraph floating between
                two fields rather than as this field's requirements — and in the
                hosted template, where the equivalent block sat outside the
                form-group, the next label ended up touching the last rule.
                Inside the pair the gap is 8px; the 16px rhythm falls after it.
                Was a single `helperText` reading "At least 8 characters", which
                understated the policy by three requirements. */}
            <Box>
              <TextField
                label={t('common:changePassword.newPassword')}
                type={showNext ? 'text' : 'password'}
                size="small" fullWidth
                value={next} onChange={(e) => setNext(e.target.value)}
                disabled={submitting}
                InputProps={{ endAdornment: eye(showNext, () => setShowNext((v) => !v)) }}
              />
              <PasswordRequirements value={next} />
            </Box>
            <TextField
              label={t('common:changePassword.confirmNewPassword')}
              type={showConfirm ? 'text' : 'password'}
              size="small" fullWidth
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
              disabled={submitting}
              InputProps={{ endAdornment: eye(showConfirm, () => setShowConfirm((v) => !v)) }}
            />
            {error && <Typography variant="caption" sx={{ color: 'error.main' }}>{error}</Typography>}
          </Stack>
        )}
      </DialogContent>
      {!success && (
        <DialogActions sx={{ pb: 2, gap: 1 }}>
          <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>{t('common:actions.cancel')}</Button>
          <Button onClick={submit} variant="contained" disabled={submitting || !current || !next || !confirm} sx={{ textTransform: 'none', borderRadius: 1 }}>
            {submitting ? t('common:changePassword.changing') : t('common:changePassword.changePassword')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ChangePasswordInlineDialog;
